import React from 'react';
import Table from '../../../shared/components/layout/Table';
import Button from '../../../shared/components/ui/Button';
import { Plus } from 'lucide-react';

/*
  BlockHierarchy renders three views based on the current selection:
  - Blocks table (with Edit/Delete in action column and inline Add Block row)
  - Levels table for the selected block (with Delete action and floating Add Level)
  - Units table for the selected level (with Edit/Delete in action column)

  Props expected from parent:
  - blocksRows: array of transformed rows for blocks (must include 'actions' cell)
  - levelsRows: array of transformed rows for levels (must include 'actions' cell when delete enabled)
  - unitsRows: array of transformed rows for units
  - unitHeaders: headers array for units table
  - selectedBlockId: current selected block id (or null)
  - isViewUnits: boolean indicating if the units table is being viewed
  - onClickBlock(id): row click handler for a block
  - onEditBlock(id), onDeleteBlock(id), onAddBlock()
  - onClickLevel(id), onDeleteLevel(id), onAddLevel()
  - onEditUnit(id), onDeleteUnit(id), onAddUnit()
  - showAddButtons: boolean to toggle floating add buttons
*/

export default function BlockHierarchy({
  blocksRows = [],
  levelsRows = [],
  unitsRows = [],
  unitHeaders = [],
  selectedBlockId = null,
  isViewUnits = false,
  onClickBlock,
  onEditBlock,
  onDeleteBlock,
  onAddBlock,
  onClickLevel,
  onDeleteLevel,
  onAddLevel,
  onEditUnit,
  onDeleteUnit,
  onAddUnit,
  showAddButtons = true
}) {
  const showBlocks = !selectedBlockId;
  const showLevels = !!selectedBlockId && !isViewUnits;
  const showUnits = !!selectedBlockId && isViewUnits;

  return (
    <div className="relative w-full">
      {/* Blocks view */}
      {showBlocks && (
        <div className="block-table-container h-[calc(100vh-11rem)] overflow-auto">
          <style>{`
            .block-table-container .table-body {
              position: static !important;
              overflow-y: visible !important;
              max-height: none !important;
              height: auto !important;
              top: auto !important;
              padding-bottom: 0 !important;
            }
            .block-table-container .table-container {
              height: auto !important;
            }
            .block-table-container > div {
              height: auto !important;
            }
          `}</style>
          <Table
            headers={[
              'Block Name',
              'Type',
              'Levels',
              'Accommodation types',
              'Operations Manager',
              'Actions'
            ]}
            data={blocksRows}
            identifier={'id'}
            isEditEnable={true}
            isDeleteEnable={true}
            onEdit={onEditBlock}
            onDelete={onDeleteBlock}
            handleRowClick={onClickBlock}
            isWrap={true}
          />
          {showAddButtons && (
            <button
              type="button"
              onClick={onAddBlock}
              aria-label="Add Block"
              className="-mt-4 mb-4 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[#BFD4D8] bg-white p-4 text-[#2A565E] shadow-sm transition-colors hover:border-[#9EC5CC] hover:bg-[#F7FBFC]">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#E8F3F5]">
                <Plus size={18} />
              </span>
              <span className="text-sm font-semibold">Add Block</span>
            </button>
          )}
        </div>
      )}

      {/* Levels view */}
      {showLevels && (
        <div className="level-table-container h-[calc(100vh-13rem)] overflow-auto ">
          <style>{`
            .level-table-container .table-body {
              position: static !important;
              overflow-y: visible !important;
              max-height: none !important;  
              height: auto !important;
              top: auto !important;
              padding-bottom: 0 !important;
            }
            .level-table-container .table-container {
              height: auto !important;
            }
            .level-table-container > div {
              height: auto !important;  
            }
          `}</style>
          <Table
            headers={['Level Name', 'Accommodation Type', 'No of Units', 'Actions']}
            data={levelsRows}
            identifier={'id'}
            isDeleteEnable={true}
            handleRowClick={onClickLevel}
            isEditEnable={false}
            onDelete={onDeleteLevel}
            isWrap={true}
          />
          {showAddButtons && (
            <div className="fixed bottom-8 right-8 z-50">
              <Button
                displayType="primary"
                onClick={onAddLevel}
                customStyle={{
                  borderRadius: '50%',
                  width: '50px',
                  height: '50px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }}>
                <Plus size={24} color="white" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Units view */}
      {showUnits && (
        <div className="unit-table-container h-[calc(100vh-13rem)] overflow-auto ">
          <style>{`
            .unit-table-container .table-body {
              position: static !important;
              overflow-y: visible !important;
              max-height: none !important;
              height: auto !important;
              top: auto !important;
              padding-bottom: 0 !important;
            }
            .unit-table-container .table-container {
              height: auto !important;
            }
            .unit-table-container > div {
              height: auto !important;
            }
          `}</style>
          <Table
            headers={unitHeaders}
            data={unitsRows}
            identifier={'id'}
            isEditEnable={true}
            isDeleteEnable={true}
            onEdit={onEditUnit}
            onDelete={onDeleteUnit}
            isWrap={true}
          />
          {showAddButtons && (
            <div className="fixed bottom-8 right-8 z-50">
              <Button
                displayType="primary"
                onClick={onAddUnit}
                customStyle={{
                  borderRadius: '50%',
                  width: '50px',
                  height: '50px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }}>
                <Plus size={24} color="white" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
